import { Component, Element, Fragment, h, Host, Prop, Watch } from "@stencil/core";

import { ContactEmail, ContactNumber, ContactWebsite, StzhContactAvailabilityItem } from "../../../index";

import { hasSlot } from "../../../utils/utils";

function formatTel(tel: string) {
  return tel.replace(/\s/g, "");
}

/**
 * @slot main-heading - slot for main heading
 * @slot contact-form - slot for contact form
 * @slot image - slot for image
 * @slot address - slot for address (alternative for `address` property or separate properties `street`, `streetInfo`, `postalCode` & `location`)
 * @slot pobox - slot for pobox (alternative for `pobox` property or separate properties `poboxHeading`, `poboxTitle`, `poboxPostalCode` & `poboxLocation`)
 * @slot slot for additional (e.g. accordion with contact form, map or datalist)
 */
@Component({
  tag: "stzh-contact",
  styleUrl: "stzh-contact.scss",
  scoped: true,
})
export class StzhContact {
  /** Type */
  @Prop() type: "Organization" | "Person" = "Organization";

  /** Main heading */
  @Prop() mainHeading: string;

  /** Main heading level */
  @Prop() mainHeadingLevel: "1" | "2" | "3" | "4" = "2";

  /** Heading */
  @Prop() heading: string;

  /** City label */
  @Prop() cityLabel: string;

  /** Departament */
  @Prop() departament: string;

  /** Resposibility title */
  @Prop() responsibilityTitle: string;

  /** Resposibility title */
  @Prop() responsibility: string;

  /** Heading level */
  @Prop() headingLevel: "1" | "2" | "3" | "4" = "3";

  /** Name */
  @Prop() name: string;

  /** Person role */
  @Prop() personRole: string;

  /** Address (alternative for address slot or separate properties `street`, `streetInfo`, `postalCode` & `location`) */
  @Prop() address: string;

  /** Street */
  @Prop() street: string | string[];
  private _street: string[] = [];

  /** Additional street info */
  @Prop() streetInfo: string | string[];
  private _streetInfo: string[] = [];

  /** Postal code */
  @Prop() postalCode: string;

  /** Location / city */
  @Prop() location: string;

  /**
   * Numbers
   * Array of objects that can include the attributes `type` = `tel`|`fax`, `label`, `number`
   * and optionally `analyticsId` for setting `s-object-id` to the link element
   * (for Adobe Analytics, default value is `label` and `number`).
   */
  @Prop() numbers: ContactNumber[] | string = [];
  private _numbers: ContactNumber[] = [];

  /**
   * Numbers
   * Array of objects that can include the attributes `label`, `number`, `text`, `href`
   * and optionally `analyticsId` for setting `s-object-id` to the link element
   * (for Adobe Analytics, default value is `label` and `text`).
   */
  @Prop() emails: ContactEmail[] | string = [];
  private _emails: ContactEmail[] = [];

  /**
   * Website
   * Array of objects that can include the attributes `label`, `number`, `text`, `href`
   * and optionally `analyticsId` for setting `s-object-id` to the link element
   * (for Adobe Analytics, default value is `label` and `text`).
   */
  @Prop() websites: ContactWebsite[] | string = [];
  private _websites: ContactWebsite[] = [];

  /** Pobox (alternative for pobox slot or separate properties `poboxHeading`, `poboxTitle`, `poboxPostalCode` & `poboxLocation`) */
  @Prop() pobox: string;

  /** Post office box heading */
  @Prop() poboxHeading: string;

  /** Post office box title */
  @Prop() poboxTitle: string;

  /** Post office box postal code */
  @Prop() poboxPostalCode: string;

  /** Post office box location / city */
  @Prop() poboxLocation: string;

  /** Availability title */
  @Prop() availabilityTitle: string;

  /** Contact title */
  @Prop() contactTitle: string;

  /** Availability title level */
  @Prop() availabilityTitleLevel: "1" | "2" | "3" | "4" = "3";

  /** Availability times */
  @Prop() availability: string | StzhContactAvailabilityItem[];
  private _availability: StzhContactAvailabilityItem[] = [];

  /** Additional freetext */
  @Prop() additionalInformation: string;

  @Watch("availability")
  availabilityWatcher(newValue: string | StzhContactAvailabilityItem[]) {
    if (typeof newValue === "string") {
      this._availability = JSON.parse(newValue);
    } else {
      this._availability = newValue;
    }
  }

  @Watch("numbers")
  numbersWatcher(newValue: ContactNumber[] | string) {
    if (typeof newValue === "string") {
      try {
        this._numbers = JSON.parse(newValue);
      } catch (jsonParseException) {
        // Nothing to do here, just catching to prevent component from dying.
      }
    } else {
      this._numbers = newValue;
    }
  }

  @Watch("emails")
  emailsWatcher(newValue: ContactEmail[] | string) {
    if (typeof newValue === "string") {
      try {
        this._emails = JSON.parse(newValue);
      } catch (jsonParseException) {
        // Nothing to do here, just catching to prevent component from dying.
      }
    } else {
      this._emails = newValue;
    }
  }
  @Watch("websites")
  websitesWatcher(newValue: ContactWebsite[] | string) {
    if (typeof newValue === "string") {
      try {
        this._websites = JSON.parse(newValue);
      } catch (jsonParseException) {
        // Nothing to do here, just catching to prevent component from dying.
      }
    } else {
      this._websites = newValue;
    }
  }

  @Watch("street")
  streetWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      try {
        this._street = JSON.parse(newValue);
      } catch (noArrayException) {
        this._street = [newValue];
      }
    } else if (newValue) {
      this._street = newValue;
    }
  }

  @Watch("streetInfo")
  streetInfoWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      try {
        this._streetInfo = JSON.parse(newValue);
      } catch (noArrayException) {
        this._streetInfo = [newValue];
      }
    } else if (newValue) {
      this._streetInfo = newValue;
    }
  }

  @Element() element: HTMLStzhFigureElement;

  async componentWillLoad() {
    this.numbersWatcher(this.numbers);
    this.emailsWatcher(this.emails);
    this.availabilityWatcher(this.availability);
    this.streetWatcher(this.street);
    this.streetInfoWatcher(this.streetInfo);
    this.websitesWatcher(this.websites);
  }

  render() {
    const hasImage = hasSlot(this.element, "image");
    const hasAddress = !!this.address || hasSlot(this.element, "address");
    const hasAddons = hasSlot(this.element, "addons");
    const hasLinks = hasSlot(this.element, "links");

    const classes = {
      "stzh-contact": true,
      "stzh-contact--has-image": hasImage,
      "stzh-contact--has-address": hasAddress,
      "stzh-contact--has-addons": hasAddons,
      "stzh-contact--has-links": hasLinks,
    };

    const HeadingLevel = `h${this.headingLevel}`;
    const AvailabilityTitleElement = `h${this.availabilityTitleLevel}`;
    const isPerson = this.type === "Person";
    const isOrganization = this.type === "Organization";
    const hasMainHeading = !!this.mainHeading || hasSlot(this.element, "main-heading");

    return (
      <Host has-availability={!!this.availability ? "true" : "false"}>
        <div class={classes} itemscope itemtype={`http://schema.org/${this.type}`}>
          <div
            class="stzh-contact__main-heading"
            itemprop={(isOrganization && !this.heading) || (isPerson && !this.name) ? "name" : null}
          >
            {hasMainHeading && (
              <div class="stzh-contact__main-heading-section">
                {this.mainHeading ? (
                  <stzh-heading level={this.mainHeadingLevel} htmlContent={this.mainHeading} />
                ) : (
                  <slot name="main-heading"></slot>
                )}
              </div>
            )}
            <div class={isPerson && "stzh-contact__main-heading-text"}>
              {this.personRole && isPerson && (
                <stzh-heading curve="h3" level="3">
                  <span class="stzh-contact__role">{this.personRole}</span>
                </stzh-heading>
              )}
              {this.cityLabel && isOrganization && (
                <stzh-heading curve="h3" level="3">
                  <span class="stzh-contact__departament">{this.cityLabel}</span>
                </stzh-heading>
              )}
              {this.departament && isOrganization && (
                <stzh-heading curve="h3" level="3">
                  <span class="stzh-contact__departament">{this.departament}</span>
                </stzh-heading>
              )}
              {((isPerson && this.name) || (isOrganization && this.heading)) && (
                <div class="stzh-contact__main-heading-type">
                  {this.name && isPerson && <stzh-heading level="3" curve="h3" htmlContent={this.name} />}
                  {this.heading && isOrganization && (
                    <stzh-heading level={this.headingLevel} htmlContent={this.heading} />
                  )}
                </div>
              )}
            </div>
            {isPerson && (
              <div class="stzh-contact__image-wrapper">
                <div class="stzh-contact__image">
                  <slot name="image"></slot>
                </div>
              </div>
            )}
          </div>

          <div class="stzh-contact__responsibility">
            {this.responsibilityTitle && (
              <HeadingLevel
                class="stzh-contact__heading"
                itemprop={this.responsibilityTitle}
                innerHTML={this.responsibilityTitle}
              />
            )}
            {this.responsibility && (
              <stzh-text class="stzh-contact__responsibility-text">{this.responsibility}</stzh-text>
            )}
          </div>

          <div class="stzh-contact__contact-form">
            <slot name="contact-form"></slot>
          </div>

          <div class="stzh-contact__additional">
            <slot></slot>
          </div>

          <div class="stzh-contact__info">
            {this.contactTitle && (
              <AvailabilityTitleElement class="stzh-contact__availability-title">
                {this.contactTitle}
              </AvailabilityTitleElement>
            )}

            <div
              class="stzh-contact__address"
              itemprop="address"
              itemscope
              itemtype="http://schema.org/PostalAddress"
              innerHTML={this.address}
            >
              {!this.address && (
                <Fragment>
                  {this._street.length > 0 && (
                    <div class="stzh-contact__street" itemprop="streetAddress">
                      {this._street.map((street: string) => (
                        <Fragment>
                          {street}
                          <br />
                        </Fragment>
                      ))}
                      {this._streetInfo.length > 0 && (
                        <Fragment>
                          <br />{" "}
                          {this._streetInfo.map((streetInfo: string) => (
                            <Fragment>
                              {streetInfo}
                              <br />
                            </Fragment>
                          ))}
                        </Fragment>
                      )}
                    </div>
                  )}

                  {this.postalCode && (
                    <span class="stzh-contact__postal-code" itemprop="postalCode">
                      {this.postalCode}&nbsp;
                    </span>
                  )}

                  {this.location && (
                    <span class="stzh-contact__locality" itemprop="addressLocality">
                      {this.location}
                    </span>
                  )}

                  <slot name="address"></slot>
                </Fragment>
              )}
            </div>

            {this._numbers.length > 0 && (
              <div class="stzh-contact__numbers">
                {this._numbers.map(number => (
                  <div class="stzh-contact__number">
                    <span>{number.label}&nbsp;</span>
                    <stzh-link
                      size="inherit"
                      height="auto"
                      class="stzh-contact__number-link"
                      href={`${number.type}:${formatTel(number.number)}`}
                      analyticsId={number.analyticsId || `${number.label} ${number.number}`}
                      itemprop={number.type === "fax" ? "faxNumber" : "telephone"}
                    >
                      {number.number}
                    </stzh-link>
                  </div>
                ))}
              </div>
            )}

            {this._emails.length > 0 && (
              <div class="stzh-contact__emails">
                {this._emails.map(email => (
                  <div class="stzh-contact__email">
                    <span>{email.label}&nbsp;</span>
                    {email.href ? (
                      <stzh-link
                        size="inherit"
                        height="auto"
                        class="stzh-container__email-link"
                        href={`mailto:${email.href}`}
                        analyticsId={email.analyticsId || `${email.label} ${email.text}`}
                        itemprop="email"
                      >
                        {email.text}
                      </stzh-link>
                    ) : (
                      <span class="stzh-container__email-link" itemprop="email">
                        {email.text}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {this.websites && (
              <div class="stzh-contact__websites">
                {this._websites.map(website => (
                  <div class="stzh-contact__website">
                    <span>{website.label}&nbsp;</span>
                    <stzh-datalist-item href={website.href} value={website.text} rel={website.rel}></stzh-datalist-item>
                  </div>
                ))}
              </div>
            )}

            <div class="stzh-contact__pobox" innerHTML={this.pobox}>
              {!this.pobox && (
                <Fragment>
                  {this.poboxHeading && <div class="stzh-contact__pobox-heading">{this.poboxHeading}</div>}
                  {this.poboxTitle && <div class="stzh-contact__pobox-title">{this.poboxTitle}</div>}
                  {this.poboxPostalCode && (
                    <span class="stzh-contact__pobox-postal-code">{this.poboxPostalCode}&nbsp;</span>
                  )}
                  {this.poboxLocation && <span class="stzh-contact__pobox-locality">{this.poboxLocation}</span>}
                  <slot name="pobox"></slot>
                </Fragment>
              )}
            </div>

            <div class="stzh-contact__additional-information" innerHTML={this.additionalInformation}></div>
          </div>

          {this.availability && (
            <div class="stzh-contact__availability">
              {this.availabilityTitle && (
                <AvailabilityTitleElement class="stzh-contact__availability-title">
                  {this.availabilityTitle}
                </AvailabilityTitleElement>
              )}

              {this._availability.length > 0 && (
                <ul class="stzh-contact__availability-list">
                  {this._availability.map(item => (
                    <li class="stzh-contact__availability-list-item">
                      <div class="stzh-contact__availability-weekday">{item.weekday}</div>
                      {item.hours.length > 0 && (
                        <div class="stzh-contact__availability-hours">
                          {item.hours.map((hour, index) => (
                            <Fragment>
                              <div class="stzh-contact__availability-hour">{hour}</div>
                              {index < item.hours.length - 1 && (
                                <div class="stzh-contact__availability-hours-separator"></div>
                              )}
                            </Fragment>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
